// DiscountBoxes.tsx
import React, { useState } from 'react';
import './marketComponent.css';

interface MarketDiscount {
	id: string;
	station_owner: string;
	station: string;
	market: string;
	producer: string;
	is_active?: number | null;
	month?: string;
	year?: string;
	[key: string]: any;
}

interface DiscountBoxesProps {
	discounts: MarketDiscount[];
	onBoxClick?: (discount: MarketDiscount) => void;
	activeTable: string;
}

const DiscountBoxes: React.FC<DiscountBoxesProps> = ({ discounts, onBoxClick, activeTable }) => {

	// Function to determine if item is active (null/undefined/1 = active, 0 = inactive)
	const isItemActive = (discount: MarketDiscount): boolean => {
		return discount.is_active !== 0;
	};

	const getMonthYear = (discount: MarketDiscount): string => {
		const month = discount.month || "";
		const year = discount.year || "";
		return month && year ? `${month} ${year}` : ""
	};

	// Helper function to convert month name to number for sorting
	const getMonthNumber = (monthName: string): number => {
		const months: { [key: string]: number } = {
			'january': 1, 'february': 2, 'march': 3, 'april': 4,
			'may': 5, 'june': 6, 'july': 7, 'august': 8,
			'september': 9, 'october': 10, 'november': 11, 'december': 12
		};
		return months[monthName.toLowerCase()] || 0;
	};

	// Helper function to get sortable date value
	const getDateSortValue = (discount: MarketDiscount): number => {
		const year = parseInt(discount.year || '0');
		const month = getMonthNumber(discount.month || '');
		
		// Return a number that represents year + month for sorting
		// Example: 2024 December = 202412, 2023 January = 202301
		return year * 100 + month;
	};

	// Sort discounts with improved sorting logic
	const sortedDiscounts = [...discounts].sort((a, b) => {
		const aActive = isItemActive(a);
		const bActive = isItemActive(b);
		
		// 1. Active items first
		if (aActive && !bActive) return -1;
		if (!aActive && bActive) return 1;
		
		// 2. Sort by date (newest first) within same active status
		if (activeTable == 'market_discounts') {
			const aDateValue = getDateSortValue(a);
			const bDateValue = getDateSortValue(b);
			
			if (aDateValue !== bDateValue) {
				return bDateValue - aDateValue; // Newest first (descending)
			}
		}
		
		// 3. If same date (or no date), sort alphabetically by station_owner or producer
		const aName = activeTable === 'market_discounts' ? a.station : a.producer;
		const bName = activeTable === 'market_discounts' ? b.station : b.producer;
		return aName.localeCompare(bName);
	});

	return (
		<div className="discount-boxes-container">
			{sortedDiscounts.map((discount) => {
				const isActive = isItemActive(discount);
				const monthYear = getMonthYear(discount);

				return (
					<div
						key={discount.id}
						className={`discount-box ${isActive ? 'active' : 'inactive'}`}
						onClick={() => onBoxClick && onBoxClick(discount)}
					>
					{activeTable == 'market_discounts' && (
						<div>
							<div>
								<div className='mc-horizontal-container'>
									<p className='mc-main-text'>{discount.station}</p>
									<p className='mc-main-text'>-</p>
									<p className='mc-main-text'>{discount.market}</p>
								</div>
								<div className='mc-horizontal-container'>
									<p className='mc-guide-text'>Station</p>
									<p className='mc-guide-text'>-</p>
									<p className='mc-guide-text'>Market</p>
								</div>
							</div>
							<div className='mc-lower-container'>
								<div>
									<div className='mc-horizontal-container'>
										<p className='mc-main-text'>{discount.station_owner}</p>
									</div>
									<div className='mc-horizontal-container'>
										<p className='mc-guide-text'>Station Owner</p>
									</div>		
								</div>	
								<div className='mc-monthYear-container'>
									{monthYear}
								</div>	
							</div>	
						</div>
					)}
					{activeTable == 'producer_pricing' && (
						<div>
							<p className='mc-main-text'>{discount.producer}</p>

							<div className='mc-lower-container'>
								<div>
									<div className='mc-horizontal-container'>
										<p className='mc-guide-text'>Producer</p>
									</div>
								</div>	
								<div className='mc-monthYear-container'>
									{monthYear}
								</div>	
							</div>	
						</div>
					)}
					</div>
				)
			})}
		</div>
	);
};

export default DiscountBoxes;
